// frontend/src/services/runService.js
// Runs, the import wizard and lesson assignment (YES-259, YES-260; call map sections 7–8), on the
// routes of docs/openapi.yaml. Counts and IDs only: no utterances or labels reach these screens.
import api from './api';
import { saveFile } from './download';

export { saveFile };

const enc = encodeURIComponent;

export const listRuns = async () => (await api.get('/api/runs')).data.data;

export const getRun = async (runId) => (await api.get(`/api/runs/${enc(runId)}`)).data;

// Admins only (data.purge), and only while the run is in setup; 409 once it has started
export const deleteRun = async (runId) => (await api.delete(`/api/runs/${enc(runId)}`)).data;

// The dry run: the twelve checks of docs/import-bundle.md. A failed check is a 200 with passed false.
export const checkBundle = async (file) => {
    const form = new FormData();
    form.append('bundle', file);
    return (await api.post('/api/imports', form)).data;
};

export const cancelCheck = async (checkId) => (await api.delete(`/api/imports/${enc(checkId)}`)).data;

// File names, row numbers and IDs only; no transcript text
export const getCheckReport = async (checkId) =>
    (await api.get(`/api/imports/${enc(checkId)}/report`, { responseType: 'blob' })).data;

// { slots: [{ name, userId }], schedule } → the new run's ID
export const commitImport = async (checkId, { slots, schedule }) => {
    const body = { slots };
    if (schedule) body.schedule = schedule;
    return (await api.post(`/api/imports/${enc(checkId)}/commit`, body)).data.runId;
};

// Annotator accounts only: researchers and admins never hold a slot (decision 2026-10-23)
export const listAdjudicators = async () => (await api.get('/api/accounts/adjudicators')).data.data;

export const getAssignment = async (runId) => (await api.get(`/api/runs/${enc(runId)}/assignment`)).data;

// Rows with two adjudicators and a tie-breaker; nothing is saved if one row is wrong
export const saveAssignment = async (runId, lessons) => (await api.put(`/api/runs/${enc(runId)}/assignment`, { lessons })).data;

export const startRun = async (runId) => (await api.post(`/api/runs/${enc(runId)}/start`)).data;

// The run's completed work as of now, one .zip; slots, never accounts; no transcript text
export const exportRun = async (runId) => {
    const response = await api.post(`/api/runs/${enc(runId)}/export`, null, { responseType: 'blob' });
    const disposition = response.headers?.['content-disposition'] || '';
    const name = disposition.match(/filename="?([^";]+)"?/)?.[1];
    return { blob: response.data, fileName: name };
};

// SHA-256 of a file, shown with the export so Study 1 can check what it received
export const sha256Of = async (blob) => {
    if (!window.crypto?.subtle || !blob.arrayBuffer) return null;
    const digest = await window.crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
};

// ADAS runs from consented lessons (YES-267; call map section 13). Every route here is planned.
// { controlShare, reliabilityTarget, tieOnUnsure, flagging }; flagging is null until Study 1 sets it
export const getRunDefaults = async () => (await api.get('/api/runs/defaults')).data;

// { name, schedule?, controlShare, reliabilityTarget, tieOnUnsure, slots: [{ name, userId }] } → the run's ID
export const createRun = async (settings) => (await api.post('/api/runs', settings)).data.runId;

// Any subset of the settings; refused (409) once a lesson is saved
export const updateRunSettings = async (runId, settings) => (await api.patch(`/api/runs/${enc(runId)}`, settings)).data;

// Consented, analysed lessons in no run; no teacher identity or file name
export const listCandidateLessons = async (runId) => (await api.get(`/api/runs/${enc(runId)}/candidate-lessons`)).data.lessons;

// One batch: [{ lessonId, reliability, annotatorIds, blindSlotIds, tieBreakSlotId }]; the first starts the run
export const addRunLessons = async (runId, lessons) => (await api.post(`/api/runs/${enc(runId)}/lessons`, { lessons })).data;
